import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Progress as Bar } from "@/components/ui/progress";
import { BarChart, Bar as RBar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line } from "recharts";
import { Trophy, BookOpen, GraduationCap, Target } from "lucide-react";

const SUBJECT_EMOJI = { "Matematik": "🔢", "Türkçe": "📖", "Fen Bilimleri": "🔬", "Sosyal Bilgiler": "🌍", "İngilizce": "🇬🇧" };

export default function ProgressPage() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/progress").then(({ data }) => setData(data)); }, []);

  if (!data) return <div className="text-center py-20 text-4xl animate-bounce">🦉</div>;

  const chartData = data.attempts.map((a, i) => ({ name: `${i + 1}`, puan: a.score }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">🏆 İlerleme Takibi</h1>
        <p className="text-slate-500 mt-1">Başarını takip et, hedefe adım adım yaklaş!</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatBig icon={BookOpen} color="bg-blue-500" value={`${data.topics_completed}/${data.topics_total}`} label="Konu Tamamlandı" />
        <StatBig icon={GraduationCap} color="bg-emerald-500" value={data.exams_taken} label="Deneme Çözüldü" />
        <StatBig icon={Target} color="bg-amber-500" value={`%${data.avg_score}`} label="Ortalama Başarı" />
        <StatBig icon={Trophy} color="bg-rose-500" value={data.exams_taken > 0 ? Math.max(...data.attempts.map(a=>a.score), 0) + "%" : "-"} label="En Yüksek Puan" />
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
        <h2 className="font-bold text-lg text-slate-900 mb-4">Ders Bazlı Tamamlama</h2>
        <div className="space-y-4">
          {data.subjects.map((s) => {
            const pct = s.total ? Math.round((s.completed / s.total) * 100) : 0;
            return (
              <div key={s.subject} data-testid={`subject-progress-${s.subject}`}>
                <div className="flex justify-between text-sm font-semibold text-slate-700 mb-1.5">
                  <span>{SUBJECT_EMOJI[s.subject]} {s.subject}</span>
                  <span className="text-slate-400">{s.completed}/{s.total}</span>
                </div>
                <Bar value={pct} className="h-3" />
              </div>
            );
          })}
        </div>
      </div>

      {data.attempts.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h2 className="font-bold text-lg text-slate-900 mb-4">Deneme Puan Grafiği</h2>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
              <YAxis domain={[0, 100]} stroke="#94a3b8" fontSize={12} />
              <Tooltip />
              <Line type="monotone" dataKey="puan" stroke="#4F46E5" strokeWidth={3} dot={{ r: 5, fill: "#4F46E5" }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {data.attempts.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
          <h2 className="font-bold text-lg text-slate-900 mb-4">Son Denemeler</h2>
          <div className="space-y-2">
            {[...data.attempts].reverse().map((a) => (
              <div key={a.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                <span className="text-sm font-medium text-slate-700">{a.exam_title}</span>
                <span className={`text-sm font-bold ${a.score >= 70 ? "text-emerald-600" : "text-amber-600"}`}>%{a.score} ({a.correct}/{a.total})</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatBig({ icon: Icon, color, value, label }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      <div className={`${color} w-10 h-10 rounded-xl flex items-center justify-center text-white`}><Icon className="w-5 h-5" /></div>
      <p className="mt-3 text-2xl font-extrabold text-slate-900">{value}</p>
      <p className="text-xs text-slate-500 font-medium">{label}</p>
    </div>
  );
}
